import React, { useState, useRef, useEffect } from 'react';
import { StylishNewButton } from './StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { getInitials } from '../../../helpers/intials';

interface Member {
  id: string;
  name: string;
}

interface TeamCardProps {
  name: string;
  aor?: string;
  description?: string;
  members: Member[];
  totalMembers: number;
  onEdit: () => void;
  onDelete: () => void;
}

const TeamCard: React.FC<TeamCardProps> = ({
  name,
  aor = '-',
  description = '',
  members,
  totalMembers,
  onEdit,
  onDelete,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(4);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const memberWidth = 60;
      const extraTextWidth = 120;

      let maxFit = Math.floor((containerWidth - extraTextWidth) / memberWidth);

      if (totalMembers <= 6) {
        maxFit = Math.min(maxFit, 6);
      } else {
        maxFit = Math.min(maxFit, 5);
      }

      maxFit = Math.max(maxFit, 1);

      setVisibleCount(maxFit);
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);

    return () => window.removeEventListener('resize', updateVisibleCount);
  }, [totalMembers]);

  const visibleMembers = members.slice(0, visibleCount);
  const extraCount =
    totalMembers > visibleCount ? totalMembers - visibleCount : 0;
  const finalVisibleMembers =
    extraCount === 1 ? members.slice(0, visibleCount + 1) : visibleMembers;
  const finalExtraCount = extraCount === 1 ? 0 : extraCount;
  return (
    <div className="team-card" ref={containerRef}>
      <div className="team-card__header">
        <span className="team-card__header__name">{name}</span>
        <span className="team-card__header__location">
          <i className="material-symbols-outlined">location_on</i>
          <span>{aor}</span>
        </span>
      </div>

      {description && (
        <div className="team-card__description">{description}</div>
      )}

      <div className="team-card__members">
        <div className="team-card__members__container">
          {finalVisibleMembers.map((member) => (
            <div key={member.id} className="team-card__members__item">
              <div className="rounded-circle avatar-initials sml team-card__members__item__avatar">
                {getInitials(member.name)}
              </div>
              <span className="team-card__members__item__name">
                {member.name}
              </span>
            </div>
          ))}
          {finalExtraCount > 0 && (
            <div className="team-card__members__item">
              <div className="rounded-circle avatar-initials sml team-card__members__item__avatar">
                +{finalExtraCount}
              </div>
              <span className="team-card__members__item__name">Members</span>
            </div>
          )}
        </div>
      </div>

      <div className="team-card__actions">
        <StylishNewButton
          customButton
          type="button"
          className="button--icon border-0"
          onClick={onEdit}
          aria-label="Edit Team"
        >
          <SharedIcon iconName="edit" />
        </StylishNewButton>
        <StylishNewButton
          customButton
          type="button"
          className="button--icon border-0"
          onClick={onDelete}
          aria-label="Delete Team"
        >
          <SharedIcon iconName="delete" />
        </StylishNewButton>
      </div>
    </div>
  );
};

export default TeamCard;
